import { useState } from "react";
import { authService } from "../../services/auth";
import { graphClient } from "../../services/graph-client";

type LoginScreenProps = {
  reason?: "missing-token" | "invalid-token";
};

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  wrong_password: "Senha incorreta.",
  user_not_found: "Número não cadastrado.",
  invalid_phone: "Número de celular inválido.",
  invalid_country: "País inválido.",
};

export const LoginScreen = ({ reason }: LoginScreenProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    reason === "invalid-token" ? "Sessão expirada. Faça login novamente." : null
  );

  const handlePhoneLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const phoneDigits = phone.replace(/\D/g, "");

    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setErrorMessage("Número de celular inválido.");
      return;
    }

    if (!password) {
      setErrorMessage("Por favor insira a senha.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await authService.login({
        phone: `55${phoneDigits}`,
        password,
        country: "br",
      });

      if (response?.error) {
        setErrorMessage(
          LOGIN_ERROR_MESSAGES[response.error] ??
            "Não foi possível entrar. Tente novamente."
        );
        return;
      }

      const token = response?.data?.token;

      if (!token) {
        // conta com 2FA não recebe token direto — precisa validar SMS no app
        setErrorMessage(
          "Esta conta exige verificação por SMS. Entre pelo aplicativo."
        );
        return;
      }

      localStorage.setItem("@token", token);
      graphClient.setHeader("Authorization", `Bearer ${token}`);

      // replace sem query string: descarta ?token= antigo pra não sobrepor o novo
      window.location.replace(window.location.pathname);
    } catch {
      setErrorMessage("Não foi possível entrar. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        background:
          "radial-gradient(circle at top, #6984ff 0%, #4f69e4 35%, #4860d6 60%, #4054bf 100%)",
        fontFamily:
          '"Segoe UI", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <section
        style={{
          width: "100%",
          maxWidth: "470px",
          padding: "28px",
          color: "#ffffff",
        }}
      >
        <div
          style={{
            width: "132px",
            height: "132px",
            margin: "8px auto 28px",
            borderRadius: "38px",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.07))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              width: "104px",
              height: "104px",
              borderRadius: "32px",
              background:
                "linear-gradient(140deg, #3652d4 0%, #4767ef 54%, #5f7cff 100%)",
              clipPath: "polygon(18% 0%, 100% 50%, 18% 100%, 18% 74%, 0% 74%, 0% 26%, 18% 26%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: "12px",
              boxShadow: "0 18px 44px rgba(30, 52, 150, 0.42)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "999px",
                  background:
                    "conic-gradient(from 215deg, #5eead4, #60a5fa, #4f46e5, #f43f5e, #fb923c, #5eead4)",
                  boxShadow: "0 0 0 3px rgba(255,255,255,0.14)",
                }}
              />
              <div
                style={{
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                  lineHeight: 0.9,
                  fontSize: "15px",
                }}
              >
                <div>DIDDER</div>
                <div>LIVE</div>
              </div>
            </div>
          </div>
        </div>

        <header style={{ textAlign: "center", marginBottom: "28px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(40px, 5vw, 56px)",
              lineHeight: 1.02,
              fontWeight: 300,
              letterSpacing: "-0.04em",
            }}
          >
            Login com senha
          </h1>
        </header>

        <form onSubmit={handlePhoneLogin}>
          <label
            htmlFor="phone"
            style={{
              display: "block",
              fontSize: "15px",
              fontWeight: 700,
              marginBottom: "10px",
              color: "#f8fbff",
            }}
          >
            Numero de celular
          </label>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              background: "rgba(248, 250, 255, 0.96)",
              borderRadius: "14px",
              padding: "18px 18px",
              boxShadow: "0 8px 26px rgba(37, 52, 129, 0.14)",
            }}
          >
            <span style={{ fontSize: "30px", lineHeight: 1 }}>🇧🇷</span>
            <span
              style={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#0f172a",
              }}
            >
              +55
            </span>
            <input
              id="phone"
              type="tel"
              placeholder="(11) 99999-9999"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              autoComplete="tel-national"
              style={inputStyle}
            />
          </div>

          <label
            htmlFor="password"
            style={{
              display: "block",
              fontSize: "15px",
              fontWeight: 700,
              marginTop: "18px",
              marginBottom: "10px",
              color: "#f8fbff",
            }}
          >
            Senha
          </label>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              background: "rgba(248, 250, 255, 0.96)",
              borderRadius: "14px",
              padding: "18px 18px",
              boxShadow: "0 8px 26px rgba(37, 52, 129, 0.14)",
            }}
          >
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Por favor insira a senha"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              style={{
                border: 0,
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#7c8698",
              }}
            >
              <EyeIcon />
            </button>
          </div>

          {errorMessage && (
            <p
              role="alert"
              style={{
                margin: "14px 2px 0",
                fontSize: "14px",
                fontWeight: 700,
                color: "#ffd7d7",
              }}
            >
              {errorMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              marginTop: "22px",
              border: 0,
              borderRadius: "18px",
              padding: "18px 20px",
              background:
                "linear-gradient(180deg, rgba(145,156,214,0.9) 0%, rgba(123,135,197,0.92) 100%)",
              color: "#ffffff",
              fontSize: "18px",
              fontWeight: 800,
              cursor: isLoading ? "wait" : "pointer",
              opacity: isLoading ? 0.7 : 1,
              boxShadow: "0 16px 34px rgba(43, 58, 139, 0.24)",
            }}
          >
            {isLoading ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </section>
    </main>
  );
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  border: 0,
  outline: "none",
  background: "transparent",
  color: "#0f172a",
  fontSize: "18px",
  fontWeight: 500,
};

function EyeIcon() {
  return (
    <svg width="28" height="20" viewBox="0 0 28 20" fill="none" aria-hidden="true">
      <path
        d="M14 1.5C7.82 1.5 2.55 5.16.5 10c2.05 4.84 7.32 8.5 13.5 8.5S25.45 14.84 27.5 10C25.45 5.16 20.18 1.5 14 1.5Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="14" cy="10" r="4.1" stroke="currentColor" strokeWidth="2.2" />
    </svg>
  );
}
